import { useEffect, useState, useRef, useCallback } from 'react';

export interface SectionLink {
  id: string;
  label: string;
}

interface PageSectionNavProps {
  sections: SectionLink[];
  offsetTop?: number;
}

export default function PageSectionNav({ sections, offsetTop = 120 }: PageSectionNavProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const originalTopRef = useRef<number>(0);
  const tickingRef = useRef(false);
  const navHeightRef = useRef(0);

  // Track which section is in view
  const updateActiveSection = useCallback(() => {
    const scrollY = window.scrollY;

    // Sticky state
    if (originalTopRef.current > 0) {
      setIsSticky(scrollY >= originalTopRef.current);
    }

    // Find active section
    const sectionTops = sections.map((s) => {
      const el = document.getElementById(s.id);
      if (!el) return Infinity;
      const rect = el.getBoundingClientRect();
      return rect.top - offsetTop;
    });

    let active = 0;
    for (let i = 0; i < sectionTops.length; i++) {
      if (sectionTops[i] <= 0) {
        active = i;
      }
    }
    setActiveIndex(active);
    tickingRef.current = false;
  }, [sections, offsetTop]);

  const handleScroll = useCallback(() => {
    if (!tickingRef.current) {
      requestAnimationFrame(updateActiveSection);
      tickingRef.current = true;
    }
  }, [updateActiveSection]);

  const checkScrollButtons = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  const scrollNav = (direction: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  useEffect(() => {
    // Measure original position after mount
    const measure = () => {
      if (navRef.current) {
        originalTopRef.current = navRef.current.getBoundingClientRect().top + window.scrollY;
      }
    };
    measure();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', measure);

    // Run once to set initial state
    updateActiveSection();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', measure);
    };
  }, [handleScroll, updateActiveSection]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    checkScrollButtons();
    el.addEventListener('scroll', checkScrollButtons, { passive: true });
    const ro = new ResizeObserver(checkScrollButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScrollButtons);
      ro.disconnect();
    };
  }, [checkScrollButtons, sections]);

  const scrollToSection = (id: string, index: number) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - offsetTop;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveIndex(index);
  };

  return (
    <div
      ref={navRef}
      className={`w-full z-40 transition-all duration-300 ${
        isSticky
          ? 'sticky top-20 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-stone-200 shadow-sm'
          : 'bg-stone-50 border-b border-stone-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center gap-1 py-3">
          {/* Left scroll arrow */}
          <button
            onClick={() => scrollNav('left')}
            className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600 transition-all duration-200 cursor-pointer ${
              canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-sm" />
          </button>

          <div
            ref={scrollContainerRef}
            className="flex items-center gap-1 overflow-x-auto md:overflow-visible md:flex-wrap flex-1 no-scrollbar scroll-smooth"
          >
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id, index)}
                className={`relative flex-shrink-0 md:flex-shrink px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeIndex === index
                    ? 'text-white'
                    : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100'
                }`}
              >
                {activeIndex === index && (
                  <span className="absolute inset-0 bg-[#3d1a1e] rounded-full transition-all duration-300 ease-out" />
                )}
                <span className="relative z-10">{section.label}</span>
              </button>
            ))}
          </div>

          {/* Right scroll arrow */}
          <button
            onClick={() => scrollNav('right')}
            className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600 transition-all duration-200 cursor-pointer ${
              canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}